import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { DeliveryOption } from "../../interfaces/deliveryOption";
import {
  checkAbbreviationExists,
  checkEmailExists,
  createClient,
  getNextAbbreviation,
} from "../../services/clientService";
import { getDeliveryOptions } from "../../services/deliveryOptionsService";
import { getStates } from "../../services/helpers";
import { useNavigate } from "react-router-dom";
import ToolTip from "../../components/component-tools/ToolTip";
import { PositionCode } from "../../interfaces/positionCode";
import {
  createPositionCodeAdmin,
  getDefaultPositionCodes,
  getServiceIDsForDefaultPositionCode,
  getServiceIDsForUCCDefaultPositionCode,
  getServiceIDsForUUADefaultPositionCode,
  getUCCDefaultPositionCodes,
  getUUADefaultPositionCodes,
  updateServicesForPositionCodeAdmin,
} from "../../services/positionCodeService";
import { getDenominations } from "../../services/denominationService";
import { Denomination } from "../../interfaces/denomination";
import { saveEmailPreferences } from "../../services/notificationsService";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { ReferenceFeeTier } from "../../interfaces/referenceFeeTier";
import { getReferenceFeeTiers } from "../../services/referenceFeeTierService";

const CreateClient = () => {
  const states = getStates();
  const [client, setClient] = useState<Client>({
    clientID: 0,
    abbreviation: "",
    primaryEmail: "",
    clientName: "",
    phoneNumber: "",
    fax: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    primaryTitle: "",
    primaryFirstName: "",
    primaryLastName: "",
    primaryJobTitle: "",
    diocese: "",
    diocese2: "",
    denomination: "",
    deliveryOptionID: 1,
    isActive: true,
    clientStatusID: 1,
    createdDate: new Date(),
    lastUpdatedDate: new Date(),
    emailNotifications: true,
    requireNotary: false,
    discountPercent: 0,
    emailFrequencyID: 0,
    questionnairesReviewed: false,
    referenceFeeTier: 2,
    primaryInvoiceManager: true,
  });
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [referenceFeeTiers, setReferenceFeeTiers] = useState<
    ReferenceFeeTier[]
  >([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const navigate = useNavigate();
  const [posCodeType, setPosCodeType] = useState("");
  const [denominations, setDenominations] = useState<Denomination[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [showConfirmationEmail, setShowConfirmationEmail] = useState(false);
  const [confirmHandlers, setConfirmHandlers] = useState<{
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);

  useEffect(() => {
    const loadDeliveryOptions = async () => {
      try {
        const options = await getDeliveryOptions();
        setDeliveryOptions(options);

        const referenceTiers = await getReferenceFeeTiers();
        setReferenceFeeTiers(referenceTiers);

        const nextAbbreviation = await getNextAbbreviation();
        setClient((prevClient) => ({
          ...prevClient,
          abbreviation: nextAbbreviation,
        }));

        const denominationsResponse = await getDenominations();
        const excludedDenominations = [2, 3, 4, 12];
        const filteredDenominations = denominationsResponse.filter(
          (denomination) =>
            !excludedDenominations.includes(denomination.denominationID),
        );
        setDenominations(filteredDenominations);
      } catch (error) {
        console.error("Error fetching client information:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDeliveryOptions();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));

    setClient((prevClient) => ({
      ...prevClient!,
      [field]: value,
    }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    const abbrevExists = await checkAbbreviationExists(client.abbreviation);

    // Client Validations
    if (!client.clientName) {
      errors.clientName = "Client name is required.";
    }
    if (!client.abbreviation) {
      errors.abbreviation = "Client Abbreviation is required.";
    } else if (abbrevExists) {
      errors.abbreviation = "Abbreviation is already in use.";
    }
    if (!posCodeType) {
      errors.posCodeType = "Position Code selection is required.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClientCreate = async () => {
    try {
      if (!client) return;

      const isValid = await validateFields();
      if (!isValid) {
        console.log("Validation failed. Please fix the errors.");
        setLoadingSpinner(false);
        const firstErrorElement = document.querySelector(".invalid-feedback");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }

      const duplicate = await checkEmailExists(
        client.primaryEmail,
        client.clientID,
      );

      if (duplicate) {
        const confirmed = await confirmEmailOverwrite();

        if (!confirmed) {
          setSuccessMessage("");
          return;
        }
      }

      setLoadingSpinner(true);

      const responseClient = await createClient(client);

      if (!responseClient) {
        setErrorMessage("Client creation failed.");
        return;
      }

      if (posCodeType !== "scratch") {
        const { codes, selectedServices } = await getPositionCodesForSave();

        // Save position codes
        const positionCodesWithClientID = codes.map((positionCode) => ({
          ...positionCode,
          clientID: responseClient.clientID,
        }));

        for (const positionCode of positionCodesWithClientID) {
          const createdPositionCode =
            await createPositionCodeAdmin(positionCode);
          const selectedServiceIDs = Array.from(
            selectedServices.get(positionCode.positionCodeID) || [],
          );
          await updateServicesForPositionCodeAdmin(
            createdPositionCode.positionCodeID,
            selectedServiceIDs,
          );
        }
      }

      // save email notification preferences
      await saveEmailPreferences(responseClient.clientID, [], 0);

      navigate(`/admin/view-client/${responseClient.clientID}`, {
        state: { successMessage: "Client created successfully." },
      });
    } catch (error) {
      console.error("Error creating client:", error);
      setSuccessMessage("");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const confirmEmailOverwrite = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const handleConfirm = () => {
        setShowConfirmationEmail(false);
        resolve(true);
      };

      const handleCancel = () => {
        setShowConfirmationEmail(false);
        resolve(false);
        window.location.reload();
      };

      setConfirmHandlers({ onConfirm: handleConfirm, onCancel: handleCancel });
      setShowConfirmationEmail(true);
    });
  };

  const getPositionCodesForSave = async () => {
    let codes: PositionCode[] = [];
    let getServiceIDsFunc:
      | ((positionCodeID: number) => Promise<number[]>)
      | null = null;

    if (posCodeType === "uua") {
      codes = await getUUADefaultPositionCodes();
      getServiceIDsFunc = getServiceIDsForUUADefaultPositionCode;
    } else if (posCodeType === "ucc") {
      codes = await getUCCDefaultPositionCodes();
      getServiceIDsFunc = getServiceIDsForUCCDefaultPositionCode;
    } else if (posCodeType === "general") {
      codes = await getDefaultPositionCodes();
      getServiceIDsFunc = getServiceIDsForDefaultPositionCode;
    }

    const serviceMap = new Map<number, Set<number>>();

    if (codes.length > 0 && getServiceIDsFunc) {
      for (const positionCode of codes) {
        const serviceIDs = await getServiceIDsFunc(positionCode.positionCodeID);
        serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
      }
    }

    return { codes, selectedServices: serviceMap };
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          <h1>Create Client</h1>
          <div className="green-label">
            <div>{"New Client"}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            <div className="text-header" style={{ flex: "0 0 100%" }}>
              Client Information
            </div>

            <div className="field-stacked" style={{ flex: "0 0 35%" }}>
              <label className="field-label">Organization Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.clientName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.clientName || ""}
                  onChange={(e) => handleInputChange(e, "clientName")}
                />
                {validationErrors.clientName && (
                  <div className="invalid-feedback">
                    {validationErrors.clientName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Denomination:</label>
              <div className="field-input">
                <select
                  className="form-control"
                  value={client?.denomination || ""}
                  onChange={(e) => handleInputChange(e, "denomination")}
                >
                  <option value="">Select a Denomination</option>
                  {denominations.map((denomination) => (
                    <option
                      key={denomination.denominationID}
                      value={denomination.denominationName}
                    >
                      {denomination.denominationName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label"> Client Name Merge Field:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.diocese || ""}
                  onChange={(e) => handleInputChange(e, "diocese")}
                  maxLength={500}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {client?.diocese?.length ?? 0}/500
                </div>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone Number:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.phoneNumber || ""}
                  onChange={(e) => handleInputChange(e, "phoneNumber")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Confidential Fax:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.fax || ""}
                  onChange={(e) => handleInputChange(e, "fax")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">State:</label>
              <div className="field-input">
                <select
                  className={`form-select`}
                  value={client?.state || ""}
                  onChange={(e) => handleInputChange(e, "state")}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>
                      {state.name} ({state.abbreviation})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Zip Code:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Final Report Delivery:</label>
              <div
                style={{
                  flex: "0 0 30%",
                  alignSelf: "end",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <select
                  className={`form-select`}
                  value={client?.deliveryOptionID || ""}
                  onChange={(e) => handleInputChange(e, "deliveryOptionID")}
                >
                  {deliveryOptions.map((option) => (
                    <option
                      key={option.deliveryOptionID}
                      value={option.deliveryOptionID}
                    >
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Require Notary:</label>
                <ToolTip
                  tooltipText="Add a notary section to candidate questionnaires."
                  placement="right"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client?.requireNotary || false}
                  onChange={(e) => handleInputChange(e, "requireNotary")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Reference Fee Tier:</label>
              <div
                style={{
                  flex: "0 0 30%",
                  alignSelf: "end",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <select
                  className={`form-select`}
                  value={client?.referenceFeeTier || ""}
                  onChange={(e) => handleInputChange(e, "referenceFeeTier")}
                >
                  {referenceFeeTiers.map((option) => (
                    <option
                      key={option.referenceFeeTierID}
                      value={option.referenceFeeTierID}
                    >
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <br></br>
            <div className="field-long">
              <label className="field-label">Abbreviation:</label>
              <ToolTip
                tooltipText="Defaults to the next available numerical abbreviation. Edit if you would like an alpha abbreviation. "
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
              <div>
                <input
                  className={`form-control ${
                    validationErrors.abbreviation ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.abbreviation}
                  onChange={(e) => handleInputChange(e, "abbreviation")}
                />
                {validationErrors.abbreviation && (
                  <div className="invalid-feedback">
                    {validationErrors.abbreviation}
                  </div>
                )}
              </div>
            </div>

            <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
              Primary Login/Contact Information *
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Title:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  placeholder="Mr./Ms., The Rev., ..."
                  value={client?.primaryTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryTitle")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">First Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryFirstName || ""}
                  onChange={(e) => handleInputChange(e, "primaryFirstName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}>
              <label className="field-label">Last Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryLastName || ""}
                  onChange={(e) => handleInputChange(e, "primaryLastName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Job Title:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryJobTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryJobTitle")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Email:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.primaryEmail || ""}
                  onChange={(e) => handleInputChange(e, "primaryEmail")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Invoice Manager:</label>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client.primaryInvoiceManager || false}
                  onChange={(e) =>
                    handleInputChange(e, "primaryInvoiceManager")
                  }
                />
              </div>
            </div>
            <div
              className="text-header"
              style={{
                flex: "0 0 100%",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <div>Position Code Selection </div>
              <ToolTip
                tooltipText="Select a starting point for client position codes. You can modify these further after creation."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                height: "30px",
              }}
            >
              <input
                type="radio"
                value="general"
                checked={posCodeType === "general"}
                onChange={() => setPosCodeType("general")}
              />{" "}
              <span>General Default Position Codes</span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                height: "30px",
              }}
            >
              <input
                type="radio"
                value="ucc"
                checked={posCodeType === "ucc"}
                onChange={() => setPosCodeType("ucc")}
              />{" "}
              <span>UCC Default Position Codes</span>
            </label>
            {/* <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                height: "30px",
              }}
            >
              <input
                type="radio"
                value="uua"
                checked={posCodeType === "uua"}
                onChange={() => setPosCodeType("uua")}
              />{" "}
              <span>UUA Default Position Codes</span>
            </label> */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                height: "30px",
              }}
            >
              <input
                type="radio"
                value="scratch"
                checked={posCodeType === "scratch"}
                onChange={() => setPosCodeType("scratch")}
              />{" "}
              <span>No default codes</span>
            </label>
            {validationErrors.posCodeType && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.posCodeType}
              </div>
            )}

            <div style={{ flex: "0 0 100%" }}></div>
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-green`}
                onClick={() => handleClientCreate()}
              >
                Create
              </button>
            </div>
          </div>

          {showConfirmationEmail && confirmHandlers && (
            <ConfirmPopup
              onConfirm={confirmHandlers.onConfirm}
              onCancel={confirmHandlers.onCancel}
              message="There is another client containing this email. Are you sure you want to link them?"
              btnColor="green"
            />
          )}
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CreateClient;
